// Firebase Analytics, started only once the visitor has consented.
//
// firebase.analytics() downloads Google's gtag script and registers a Firebase installation
// straight away, even with collection switched off, so it isn't called at all until consent
// is given (the site's cookie banner or Google's consent tool, see consent.ts), and never after
// a refusal. Until then every event is dropped. Portals, Discord and local (offline) mode never
// report consent, so analytics never starts there.
import firebase, { firebaseLoaded, onFirebaseInit } from "./fb";
import { isLocalMode } from "./localStore";
import { setPosthogConsent, posthogCapture } from "./posthog";
import { portalTrack } from "./portal";

let analytics: firebase.analytics.Analytics | null = null;
let consented = false;
let waitingForFirebase = false;
// Events logged after consent but before Firebase has arrived (the start screen shows first):
// kept and sent once it's there, so landing and first-click events still reach GA.
type PendingEvent = { name: string; params?: any };
let pending: PendingEvent[] = [];

// Rafiki Holdings' own GA4 property (account "Rafiki Holdings Limited", set up 31 Oct 2026). The
// Firebase-linked property above was created under a Google login the company can't administer,
// so page views and events are also sent here, through the same gtag Firebase installs and only
// after consent like everything else.
export const COMPANY_GA_ID = "G-63DZCR9JLQ";
let companyConfigured = false;
const gtagFn = (): any => (typeof window !== "undefined" ? (window as any).gtag : undefined);
// Firebase defines window.gtag straight away but only queues gtag's own "js" start command once
// its installation id arrives. A "config" queued before that "js" (and with no parameters) makes
// Google's script throw "Cannot read properties of undefined (reading 'is_legacy_loaded')" and the
// company property loses page views, so wait for the "js" command first, then configure with {}.
const gtagStarted = (): boolean => {
  const dl: any[] = (typeof window !== "undefined" && (window as any).dataLayer) || [];
  return Array.isArray(dl) && dl.some((entry: any) => entry && entry[0] === "js");
};
const configureCompanyProperty = (triesLeft = 120) => {
  if (companyConfigured) return;
  const g = gtagFn();
  if (typeof g === "function" && gtagStarted()) {
    try {
      g("config", COMPANY_GA_ID, {});
      companyConfigured = true;
    } catch (e) {}
    return;
  }
  if (triesLeft > 0) setTimeout(() => configureCompanyProperty(triesLeft - 1), 250);
};

// The consent signal (consent.ts onConsentChange): true starts analytics (once), false
// switches collection off again if it had been started earlier in this visit.
export const setAnalyticsConsent = (granted: boolean) => {
  if (isLocalMode) return;
  setPosthogConsent(granted);
  if (!granted) {
    consented = false;
    pending = [];
    try {
      analytics?.setAnalyticsCollectionEnabled(false);
    } catch (e) {}
    return;
  }
  consented = true;
  // Firebase arrives after the start screen (see fb.ts): start once it's there (without
  // fetching it any sooner), if the visitor hasn't changed their mind in the meantime.
  if (!firebaseLoaded()) {
    if (!waitingForFirebase) {
      waitingForFirebase = true;
      onFirebaseInit((fb) => consented && startFirebaseAnalytics(fb));
    }
    return;
  }
  startFirebaseAnalytics(firebase);
};
const startFirebaseAnalytics = (fb: any) => {
  try {
    const a = analytics || fb.analytics();
    analytics = a;
    a.setAnalyticsCollectionEnabled(true);
  } catch (e) {}
  configureCompanyProperty();
  const queued = pending;
  pending = [];
  queued.forEach((e) => sendToGoogle(e.name, e.params));
};

export const analyticsStarted = () => analytics !== null;

// Safe to call at any time: dropped before consent.
export const logEvent = (name: string, params?: any) => {
  // Playgama build: a few progression events go to Playgama's own telemetry (no consent needed
  // there: no personal data, the Bridge's session only). Every other build: nothing.
  portalTrack(name);
  posthogCapture(name, params);
  if (!analytics) {
    if (consented && pending.length < 100) pending.push({ name, params });
    return;
  }
  sendToGoogle(name, params);
};
const sendToGoogle = (name: string, params?: any) => {
  if (!analytics) return;
  try {
    analytics.logEvent(name, params);
  } catch (e) {}
  const g = gtagFn();
  if (companyConfigured && typeof g === "function") {
    try {
      g("event", name, { ...(params || {}), send_to: COMPANY_GA_ID });
    } catch (e) {}
  }
};

// Tests only.
export const resetAnalyticsForTests = () => {
  analytics = null;
  companyConfigured = false;
  pending = [];
  consented = false;
  waitingForFirebase = false;
};
